import { Component, afterNextRender, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { animateCountUp } from '../../utils/count-up';
import { InfoTooltipComponent } from '../info-tooltip/info-tooltip.component';

@Component({
  selector: 'app-kpi-card',
  standalone: true,
  imports: [CommonModule, InfoTooltipComponent],
  template: `
    <div
      class="bg-card-bg-default rounded-md shadow-sm h-[110px] flex"
      [class.cursor-pointer]="!!routerLink()"
      (click)="onCardClick()"
    >
      @if (variant() === 'photo') {
        <div class="flex items-center justify-between gap-3 w-full pl-4 pr-4 py-3 overflow-hidden">
          <div class="flex flex-col justify-between h-full min-w-0">
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="text-xs font-medium text-text-half-dimmed truncate">{{ label() }}</span>
              @if (infoTooltip()) {
                <app-info-tooltip
                  [text]="infoTooltip()"
                  size="xs"
                  [position]="infoTooltipPosition()"
                  [align]="infoTooltipAlign()"
                />
              }
            </div>
            <span
              class="text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight transition-all duration-300"
              [class.blur-md]="loading()" [class.opacity-40]="loading()" [class.animate-pulse]="loading()"
            >{{ displayValue() }}</span>
            <span class="text-xs text-text-half-dimmed truncate">{{ caption() }}</span>
          </div>
          @if (photoUrl()) {
            <img [src]="photoUrl()" [alt]="label()" class="w-20 h-20 rounded-full object-cover shrink-0" />
          } @else {
            <div class="w-20 h-20 rounded-full bg-card-bg-hover shrink-0"></div>
          }
        </div>
      } @else {
        <div class="flex flex-col justify-between w-full p-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="text-xs font-medium text-text-half-dimmed truncate">{{ label() }}</span>
              @if (infoTooltip()) {
                <app-info-tooltip
                  [text]="infoTooltip()"
                  size="xs"
                  [position]="infoTooltipPosition()"
                  [align]="infoTooltipAlign()"
                />
              }
            </div>
            <ng-content select="[icon]"></ng-content>
          </div>
          <div
            class="text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight transition-all duration-300"
            [class.blur-md]="loading()" [class.opacity-40]="loading()" [class.animate-pulse]="loading()"
          >
            {{ displayValue() }}
          </div>
          <div class="text-xs text-text-half-dimmed truncate">
            {{ caption() }}
          </div>
        </div>
      }
    </div>
  `,
})
export class KpiCardComponent {
  private router = inject(Router);

  label = input.required<string>();
  value = input.required<number>();
  signed = input<boolean>(false);
  suffix = input<string>('');
  caption = input<string>('');
  routerLink = input<string | any[] | null>(null);
  animationDelayMs = input<number>(0);
  variant = input<'icon' | 'photo'>('icon');
  photoUrl = input<string>('');
  loading = input<boolean>(false);
  infoTooltip = input<string>('');
  infoTooltipPosition = input<'top' | 'bottom'>('bottom');
  infoTooltipAlign = input<'center' | 'left' | 'right'>('left');

  private displayed = signal(0);
  private hasAnimatedOnce = false;

  displayValue = computed(() => {
    const n = Math.round(this.displayed());
    const sign = this.signed() && n >= 0 ? '+' : '';
    return `${sign}${n}${this.suffix()}`;
  });

  constructor() {
    afterNextRender(() => {
      animateCountUp(
        this.value(),
        (v) => this.displayed.set(v),
        this.animationDelayMs(),
        800,
        () => { this.hasAnimatedOnce = true; }
      );
    });

    effect(() => {
      const target = this.value();
      if (this.hasAnimatedOnce) {
        this.displayed.set(target);
      }
    });
  }

  onCardClick(): void {
    const link = this.routerLink();
    if (!link) return;
    if (Array.isArray(link)) {
      this.router.navigate(link);
    } else {
      this.router.navigateByUrl(link);
    }
  }
}
